import React from 'react';
import { motion } from 'framer-motion';
import { VerificationStats as Stats } from '../../types/verification';
import { Users, Package, Plane, AlertTriangle } from 'lucide-react';

type Props = {
  stats: Stats;
};

export const VerificationStats = ({ stats }: Props) => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show" 
      className="grid grid-cols-1 md:grid-cols-4 gap-6"
    >
      <StatCard
        variants={item}
        icon={Users}
        label="إجمالي الحجاج"
        value={stats.totalPilgrims}
        color="primary"
      />
      <StatCard
        variants={item}
        icon={Package}
        label="الباقات غير المتطابقة"
        value={stats.invalidPackages}
        color={stats.invalidPackages > 0 ? 'red' : 'green'}
      />
      <StatCard
        variants={item}
        icon={Plane}
        label="رحلات PNR غير المتطابقة"
        value={stats.invalidPNRs}
        color={stats.invalidPNRs > 0 ? 'red' : 'green'}
      />
      <StatCard
        variants={item}
        icon={AlertTriangle}
        label="نسبة عدم التطابق"
        value={`${stats.errorPercentage}%`}
        color={stats.errorPercentage > 0 ? 'yellow' : 'green'}
      />
    </motion.div>
  );
};

type StatCardProps = {
  icon: React.ElementType;
  label: string;
  value: number | string;
  color: 'primary' | 'red' | 'green' | 'yellow';
  variants: any;
};

const StatCard = ({ icon: Icon, label, value, color, variants }: StatCardProps) => {
  const colorClasses = {
    primary: {
      bg: 'bg-primary-50',
      text: 'text-primary-500',
      ring: 'ring-primary-500/20'
    },
    red: {
      bg: 'bg-red-50',
      text: 'text-red-500',
      ring: 'ring-red-500/20'
    },
    green: {
      bg: 'bg-green-50',
      text: 'text-green-500',
      ring: 'ring-green-500/20'
    },
    yellow: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-500',
      ring: 'ring-yellow-500/20'
    }
  };

  return (
    <motion.div
      variants={variants}
      className={`
        bg-white p-6 rounded-xl shadow-sm
        ring-1 ring-inset ${colorClasses[color].ring}
        hover:shadow-md transition-shadow
      `}
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorClasses[color].bg}`}>
          <Icon className={colorClasses[color].text} size={24} />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${colorClasses[color].text}`}>
            {value}
          </p>
        </div>
      </div>
    </motion.div>
  );
};